import { useId } from "react";
import { Link } from "react-router-dom";
import { Check, CircleDashed, Clock3, CheckCircle2 } from "lucide-react";
import { useProgress } from "@/modules/premiere-video/store";

const BTN = {
  primary: "bg-bx text-white hover:bg-bx-dark shadow-[0_6px_20px_-8px_rgba(101,31,56,0.6)] disabled:bg-[#B9A3AB] disabled:shadow-none",
  outline: "border border-bx/80 text-bx bg-white hover:bg-bx hover:text-white disabled:opacity-50",
  ghost: "text-bx hover:bg-bx/5",
  light: "bg-white text-bx hover:bg-ivory",
  gold: "bg-gold text-ink hover:bg-[#B8945B]",
};

export const btnClass = (variant = "primary", size = "md") =>
  `inline-flex items-center justify-center gap-2 rounded-full font-medium transition-[background-color,color,transform,box-shadow] duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100 ${
    size === "lg" ? "min-h-[52px] px-7 text-base" : size === "sm" ? "min-h-[40px] px-4 text-sm" : "min-h-[46px] px-5 text-[15px]"
  } ${BTN[variant]}`;

export function Btn({ variant, size, className = "", ...p }) {
  return <button type="button" className={`${btnClass(variant, size)} ${className}`} {...p} />;
}

export function BtnLink({ variant, size, className = "", ...p }) {
  return <Link className={`${btnClass(variant, size)} ${className}`} {...p} />;
}

export function Card({ className = "", ...p }) {
  return <div className={`print-card rounded-[18px] border border-line bg-white p-5 shadow-[0_4px_20px_rgba(36,33,39,0.035)] sm:p-7 ${className}`} {...p} />;
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#9A7A45] ${className}`}>
      <span className="h-px w-8 bg-gold" aria-hidden />
      {children}
    </p>
  );
}

export function Field({ label, hint, value, onChange, multiline, rows = 3, placeholder, testId, inputId }) {
  const auto = useId();
  const id = inputId || auto;
  const C = multiline ? "textarea" : "input";
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-[15px] font-medium text-ink">
        {label}
      </label>
      {hint && <p className="text-sm text-soft">{hint}</p>}
      <C
        id={id}
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={multiline ? rows : undefined}
        placeholder={placeholder}
        className="w-full resize-y rounded-2xl border border-line bg-white px-4 py-3 text-base text-ink placeholder:text-[#A9A2AB] outline-none transition-[border-color,box-shadow] focus:border-bx/60 focus:shadow-[0_0_0_4px_rgba(101,31,56,0.08)]"
      />
    </div>
  );
}

export function CheckItem({ checked, onChange, label, desc, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`flex w-full items-start gap-3.5 rounded-2xl border px-4 py-3.5 text-left transition-[background-color,border-color] duration-200 ${
        checked ? "border-bx/25 bg-bx/[0.035]" : "border-line bg-white hover:border-bx/30"
      }`}
    >
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-colors ${checked ? "border-bx bg-bx" : "border-[#D6CBC1] bg-white"}`}>
        {checked && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
      </span>
      <span className="min-w-0">
        <span className={`block font-medium ${checked ? "text-bx" : "text-ink"}`}>{label}</span>
        {desc && <span className="mt-0.5 block text-sm leading-relaxed text-soft">{desc}</span>}
      </span>
    </button>
  );
}

export function ProgressBar({ value, className = "", testId }) {
  return (
    <div className={`h-2 w-full overflow-hidden rounded-full bg-[#EFE8E1] ${className}`} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} data-testid={testId}>
      <div className="h-full rounded-full bg-gradient-to-r from-bx to-bx-2 transition-[width] duration-700 ease-out" style={{ width: `${value}%` }} />
    </div>
  );
}

const STATUS = {
  todo: { label: "À commencer", Icon: CircleDashed, cls: "bg-[#F4EFEA] text-soft" },
  progress: { label: "En cours", Icon: Clock3, cls: "bg-gold-soft text-[#7A5C2E]" },
  done: { label: "Terminée", Icon: CheckCircle2, cls: "bg-bx/[0.08] text-bx" },
};

export function StatusBadge({ status, testId }) {
  const { label, Icon, cls } = STATUS[status];
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      {label}
    </span>
  );
}

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <header className="mb-8 animate-fade-up sm:mb-10">
      {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
      <h1 className="text-3xl font-semibold leading-tight tracking-tight text-bx sm:text-4xl lg:text-5xl">{title}</h1>
      {subtitle && <p className="mt-3 max-w-2xl text-base text-soft sm:text-lg">{subtitle}</p>}
      {children}
    </header>
  );
}

const SYNC_LABEL = { saving: "Enregistrement en cours…", synced: "À jour dans ton compte", error: "Synchronisation impossible pour le moment : tes réponses restent sur cet appareil." };

export function SaveNotice({ className = "" }) {
  const { sync } = useProgress();
  return (
    <div data-testid="storage-notice" className={`rounded-2xl border border-gold/30 bg-gold-soft/50 px-4 py-3 text-sm leading-relaxed text-[#5E4A2C] ${className}`}>
      <p>Tes réponses sont enregistrées dans ton compte : tu les retrouves en te connectant depuis n'importe quel appareil. Tu peux aussi exporter une sauvegarde.</p>
      {SYNC_LABEL[sync] && <p className="mt-1 text-xs font-semibold" data-testid="sync-status">{SYNC_LABEL[sync]}</p>}
    </div>
  );
}
